/**
 * Тест урока — §9.2–§9.4 ТЗ.
 *
 * Три свойства, ради которых экран устроен именно так:
 *
 * - **ответ уходит на сервер сразу**, при нажатии на вариант. Потеря
 *   связи посреди теста не должна стирать уже отвеченное (§7.7), и
 *   прерванная попытка продолжается с места остановки;
 * - **правильность не показывается по ходу.** Сервер её и не присылает:
 *   разбор появляется после завершения попытки, иначе тест превращается
 *   в подбор;
 * - **результат виден сразу** после завершения, вместе с разбором ошибок
 *   и объяснением — без него следующая попытка будет угадыванием (§9.4).
 *
 * Попыток неограниченно: смысл теста здесь — научить, а не отсеять.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Empty, ErrorState, Loading } from '@shell/index';
import { studyApi } from '../api';
import type { TestResult } from '../api';

function ResultCard({ result, onRetry, onBack }: {
  result: TestResult;
  onRetry: () => void;
  onBack: () => void;
}) {
  return (
    <Card
      title={result.passed ? `Сдано: ${result.score}%` : `Не сдано: ${result.score}%`}
      actions={
        <Button small variant="quiet" onClick={onBack}>
          К урокам
        </Button>
      }
    >
      <p className="faint">
        Проходной балл — {result.pass_score}%. Попыток сделано: {result.attempts_made}, лучший
        результат: {result.best_score}%.
      </p>

      {result.mistakes.length > 0 && (
        <>
          <p>
            <strong>Разбор ошибок</strong>
          </p>
          {result.mistakes.map((mistake) => (
            <div key={mistake.question} className="dish-line">
              <div className="dish-line__name">{mistake.question}</div>
              <div className="dish-line__meta">
                <span className="faint">
                  {mistake.explanation ?? `Верный ответ: ${mistake.correct.join(', ')}`}
                </span>
              </div>
            </div>
          ))}
        </>
      )}

      {!result.passed && (
        <Button variant="primary" onClick={onRetry}>
          Пройти ещё раз
        </Button>
      )}
    </Card>
  );
}

export function Test({ lessonId, onDone }: { lessonId: number; onDone: () => void }) {
  const [result, setResult] = useState<TestResult | null>(null);
  const [attempt, setAttempt] = useState(0); // счётчик перезапусков теста

  const test = useQuery({
    queryKey: ['test', lessonId, attempt],
    queryFn: () => studyApi.openTest(lessonId),
  });

  const answer = useMutation({
    mutationFn: ({ questionId, chosen }: { questionId: number; chosen: number[] }) =>
      studyApi.answer(test.data?.attempt_id as number, questionId, chosen),
    // Локально ответ уже отмечен: ждать ответа сервера, чтобы подсветить
    // выбранный вариант, значит подвесить интерфейс на плохой сети.
    onError: () => test.refetch(),
  });

  const finish = useMutation({
    mutationFn: () => studyApi.finishTest(test.data?.attempt_id as number),
    onSuccess: setResult,
  });

  const [chosen, setChosen] = useState<Record<number, number[]>>({});

  if (result) {
    return (
      <ResultCard
        result={result}
        onRetry={() => {
          setResult(null);
          setChosen({});
          setAttempt((value) => value + 1);
        }}
        onBack={onDone}
      />
    );
  }

  if (test.isPending) return <Loading />;
  if (test.error || !test.data) return <ErrorState onRetry={() => test.refetch()} />;

  if (test.data.attempt_id === null) {
    return <Empty title="Тест" text={test.data.note ?? undefined} />;
  }

  // Что уже отвечено: ответы сервера (возобновлённая попытка) плюс
  // нажатия в этом сеансе. Порядок именно такой — свежее нажатие
  // перекрывает то, что пришло с сервера.
  const answers: Record<number, number[]> = {};
  for (const question of test.data.questions) {
    answers[question.id] = question.chosen;
  }
  for (const [questionId, picked] of Object.entries(chosen)) {
    answers[Number(questionId)] = picked;
  }

  const answered = test.data.questions.filter(
    (question) => (answers[question.id] ?? []).length > 0,
  ).length;

  return (
    <Card
      title="Тест урока"
      actions={
        <Button small variant="quiet" onClick={onDone}>
          Выйти
        </Button>
      }
    >
      <p className="faint">
        Отвечено {answered} из {test.data.questions.length}. Ответы сохраняются сразу — если
        связь пропадёт, вы продолжите с этого места.
      </p>

      {test.data.questions.map((question) => (
        <div key={question.id} className="dish-line">
          <div className="dish-line__name">{question.body}</div>
          <div className="chips">
            {question.options.map((option) => {
              const picked = (answers[question.id] ?? []).includes(option.id);
              return (
                <Button
                  key={option.id}
                  small
                  variant={picked ? 'primary' : 'quiet'}
                  onClick={() => {
                    const next = [option.id];
                    setChosen((current) => ({ ...current, [question.id]: next }));
                    answer.mutate({ questionId: question.id, chosen: next });
                  }}
                >
                  {option.body}
                </Button>
              );
            })}
          </div>
        </div>
      ))}

      <Button
        variant="primary"
        disabled={answered < test.data.questions.length || finish.isPending}
        onClick={() => finish.mutate()}
      >
        Завершить тест
      </Button>
    </Card>
  );
}
